// 对局观测 · 人物与记录：人物名、记录排序与取最新、剧情记录进度、证据来源、人物记录卡。
// 照 Demo 观测页 RunObserver.vue 与 Demo runtime 的 view 逻辑搬来，输入只有后台详情里的快照与作品内容（pack），全是纯函数。
// asArray 与 characterName 也供另外三个模块使用。

export const asArray = (value) => (Array.isArray(value) ? value : [])

export function characterName(pack, id) {
  return asArray(pack.characters).find((item) => item.id === id)?.name || id
}

/** 记录先按来源轮次、再按修订号排序。 */
export function recordOrder(a, b) {
  return (a.through_seq ?? a.seq ?? 0) - (b.through_seq ?? b.seq ?? 0) || (a.revision ?? 0) - (b.revision ?? 0)
}

/** 同一个键只留排序最靠后的一条。 */
export function latestRecords(records, keyOf) {
  const latest = new Map()
  for (const record of [...records].sort(recordOrder)) latest.set(keyOf(record), record)
  return [...latest.values()]
}

/** 按顺序已完成剧情记录的轮数，遇到第一轮未完成就停（引擎 recorded_through）。 */
export function recordedThrough(branch) {
  let through = 0
  for (const turn of asArray(branch.turns)) {
    if (turn.recording !== undefined && turn.recording !== 'completed') break
    through += 1
  }
  return through
}

/** 后台剧情记录的整体状态：有失败的记录任务为 failed，还有未记录的轮次为 pending（服务端 view.recording + Demo recording_status）。 */
export function recordingState(branch) {
  if (asArray(branch.jobs).some((job) => job.kind === 'narrative_record' && job.status === 'failed')) return 'failed'
  if (asArray(branch.turns).length > recordedThrough(branch)) return 'pending'
  return null
}

/** 见闻记录与记忆整理的处理中、失败说明：失败的记录任务覆盖到的轮次不再算"待记录"。 */
export function recordingNotes(branch) {
  // 平台的玩家回应建议是分支里真实的 suggestions 任务，不再像 Demo 那样另拼一条伪任务。
  const jobs = asArray(branch.jobs)
  const failed_witness_jobs = jobs.filter((job) => job.kind === 'narrative_record' && job.status === 'failed')
  const pending_witness_turns = asArray(branch.turns).filter((turn) =>
    (turn.recording === 'pending' || asArray(turn.segments).some((segment) => segment.metadata_pending)) &&
    !failed_witness_jobs.some((job) => job.from_seq <= turn.seq && job.through_seq >= turn.seq))
  const memory_jobs = jobs.filter((job) => (job.kind || 'memory') === 'memory' && ['pending', 'failed'].includes(job.status))
  return { failed_witness_jobs, pending_witness_turns, memory_jobs }
}

/** 证据来源表：开场段落、作者开局事实、已记录事件、已完成记录的正文段落，各带知情人物。 */
export function evidenceSources(pack, branch) {
  const sources = new Map()
  const everyone = asArray(pack.characters).map((person) => person.id)
  const opening = pack.opening ?? {}
  if (Array.isArray(opening.segments)) {
    opening.segments.forEach((segment, index) => {
      const evidence_id = `opening-${index}`
      sources.set(evidence_id, { evidence_id, seq: 0, source_kind: 'opening', text: segment.text, audience_ids: segment.audience_ids || branch.opening?.audience_ids || everyone })
    })
  } else if (branch.opening?.text || opening.text) {
    const evidence_id = branch.opening?.segment_id || 'opening'
    sources.set(evidence_id, { evidence_id, seq: 0, source_kind: 'opening', text: branch.opening?.text || opening.text, audience_ids: branch.opening?.audience_ids || everyone })
  }
  for (const fact of asArray(pack.facts)) {
    sources.set(`fact-${fact.id}`, { evidence_id: `fact-${fact.id}`, seq: 0, source_kind: 'author_fact', text: fact.text, audience_ids: fact.observer_ids || [] })
  }
  for (const event of asArray(branch.events)) {
    if (event.event_id) sources.set(event.event_id, { evidence_id: event.event_id, seq: event.seq, source_kind: 'event', text: event.text, audience_ids: event.audience_ids || [] })
  }
  for (const turn of asArray(branch.turns)) {
    for (const segment of asArray(turn.segments)) {
      if (segment.segment_id && !segment.metadata_pending) {
        sources.set(segment.segment_id, { evidence_id: segment.segment_id, seq: turn.seq, source_kind: 'turn', text: segment.text, audience_ids: segment.audience_ids || [] })
      }
    }
  }
  return sources
}

export function sourceRows(sources, record) {
  return asArray(record.evidence_ids).map((evidence_id) => ({
    evidence_id,
    ...(sources.get(evidence_id) ?? { seq: null, source_kind: 'unknown', text: '' }),
  }))
}

const recordOwner = (record) => record?.from_id ?? record?.observer_id ?? null

/** 记录仍然有效：没被同一人物的更晚记录取代，证据也没被更正或冲突记录推翻（Demo record_is_current）。 */
function isRecordCurrent(branch, record) {
  const owner = recordOwner(record)
  const overrides = [...asArray(branch.corrections), ...asArray(branch.conflicts)]
  const superseded = new Set()
  for (const item of [...asArray(branch.observations), ...overrides]) {
    if (recordOwner(item) === owner) for (const id of asArray(item.supersedes_ids)) superseded.add(id)
  }
  if (record.record_id && superseded.has(record.record_id)) return false
  const evidence = asArray(record.evidence_ids)
  return overrides
    .filter((item) => recordOwner(item) === owner && !superseded.has(item.record_id))
    .every((correction) => {
      const old_ids = asArray(correction.old_evidence_ids)
      return (record.record_id && record.record_id === correction.record_id) ||
        !old_ids.some((ref) => evidence.includes(ref)) ||
        asArray(correction.evidence_ids).some((ref) => !old_ids.includes(ref) && evidence.includes(ref))
    })
}

/** 人物看得到这条记录：全部证据都在其知情范围内，且记录仍有效（Demo record_visible_to）。 */
export function isRecordVisible(sources, branch, record, observer_id) {
  const evidence = asArray(record.evidence_ids)
  const sourced = evidence.length > 0 && evidence.every((id) => asArray(sources.get(id)?.audience_ids).includes(observer_id))
  return sourced && isRecordCurrent(branch, record)
}

function witnessedSegments(branch, observer_id) {
  const rows = []
  for (const turn of asArray(branch.turns)) {
    asArray(turn.segments).forEach((segment, index) => {
      if (segment.metadata_pending || !asArray(segment.audience_ids).includes(observer_id)) return
      rows.push({ ...segment, seq: turn.seq, record_key: `${turn.turn_id || turn.seq}-${segment.segment_id || index}` })
    })
  }
  return rows
}

/** 一位人物的记录卡：开局设定、后续记录与后续经历，只收这位人物看得到且仍有效的记录（Demo focused_*）。 */
export function characterRecord(pack, branch, sources, observer_id) {
  const visible = (record) => record.observer_id === observer_id && isRecordVisible(sources, branch, record, observer_id)
  const withLabel = (relation) => ({ ...relation, label: `${characterName(pack, relation.from_id)} → ${characterName(pack, relation.to_id)}` })
  const later_relations = asArray(branch.relations).filter(visible).map(withLabel).sort(recordOrder)
  const goals = asArray(branch.goal_records).filter(visible).sort(recordOrder)
  const memory = asArray(branch.owner_memories).find((item) => item.observer_id === observer_id)
  const rolling_memory = memory
    ? asArray(memory.summary)
      .map((summary, index) => ({ ...summary, ...memory, record_key: `${memory.job_id || observer_id}-${index}` }))
      .filter((item) => isRecordVisible(sources, branch, item, observer_id))
      .sort(recordOrder)
    : []
  return {
    current_goal: goals.at(-1)?.text || '',
    initial_facts: asArray(pack.facts).filter((fact) => asArray(fact.observer_ids).includes(observer_id)),
    initial_relations: asArray(pack.relations).filter((relation) => asArray(relation.observer_ids).includes(observer_id)).map(withLabel),
    latest_relations: latestRecords(later_relations, (relation) => `${relation.from_id}:${relation.to_id}`),
    goal_records: [...goals].reverse(),
    open_threads: latestRecords(asArray(branch.story_threads).filter(visible), (thread) => thread.thread_id)
      .filter((thread) => thread.status === 'open')
      .sort(recordOrder),
    witnessed: witnessedSegments(branch, observer_id),
    observations: [
      ...asArray(branch.observations).map((item) => ({ ...item, record_source: 'observation' })),
      ...asArray(branch.corrections).map((item) => ({ ...item, record_source: 'correction' })),
      ...asArray(branch.conflicts).map((item) => ({ ...item, record_source: 'conflict' })),
    ].filter(visible).sort(recordOrder),
    rolling_memory,
  }
}
